import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Plus, Trash2, Save, Loader2, Eye, Image, Clock, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPES = [
    { value: 'historical_image', label: 'Historical Image' },
    { value: 'reconstruction', label: 'Reconstruction' },
    { value: 'info_popup', label: 'Info Popup' },
];

export default function AdminARAssets() {
    const { attractionId } = useParams();
    const [assets, setAssets] = useState([]);
    const [attraction, setAttraction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ type: 'historical_image', title: '', description: '', image_url: '', overlay_text: '', year_label: '', position_order: 0 });

    useEffect(() => {
        fetchData();
    }, [attractionId]);

    const fetchData = async () => {
        try {
            const [assetsRes, attrRes] = await Promise.all([
                axios.get(`${API}/api/admin/attractions/${attractionId}/ar-assets`, { withCredentials: true }),
                axios.get(`${API}/api/admin/attractions`, { withCredentials: true })
            ]);
            setAssets(assetsRes.data);
            const attr = attrRes.data.find(a => a.id === attractionId);
            setAttraction(attr || null);
        } catch (e) {
            toast.error('Failed to load AR assets');
        } finally { setLoading(false); }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${API}/api/admin/attractions/${attractionId}/ar-assets`, { ...form, position_order: assets.length }, { withCredentials: true });
            toast.success('AR asset created');
            setForm({ type: 'historical_image', title: '', description: '', image_url: '', overlay_text: '', year_label: '', position_order: 0 });
            setShowForm(false);
            fetchData();
        } catch (e) {
            toast.error('Failed to create AR asset');
        } finally { setSaving(false); }
    };

    const handleDelete = async (assetId) => {
        if (!window.confirm('Delete this AR asset?')) return;
        try {
            await axios.delete(`${API}/api/admin/attractions/${attractionId}/ar-assets/${assetId}`, { withCredentials: true });
            toast.success('AR asset deleted');
            fetchData();
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-ar-assets-page">
                <div className="flex items-center gap-4">
                    <Link to="/admin/attractions">
                        <Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
                    </Link>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Eye className="w-6 h-6 text-purple-400" /> AR Assets
                        </h1>
                        <p className="text-sm text-white/50">{attraction?.title || 'Loading...'}</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-ar-asset-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add AR Asset
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-[#12151C] border border-white/10 rounded-lg p-6" data-testid="ar-asset-form">
                        <h3 className="text-lg font-semibold text-white mb-4">New AR Asset</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Type *</Label>
                                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 text-sm" data-testid="ar-type-select">
                                        {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Title *</Label>
                                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Original 1613 Villa" className="bg-[#0A0D14] border-white/10 text-white" data-testid="ar-title-input" required />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Year Label</Label>
                                    <Input value={form.year_label} onChange={e => setForm({ ...form, year_label: e.target.value })} placeholder="e.g. 1613" className="bg-[#0A0D14] border-white/10 text-white" data-testid="ar-year-input" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Image URL *</Label>
                                <Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className="bg-[#0A0D14] border-white/10 text-white" data-testid="ar-image-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Description</Label>
                                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Historical context..." className="w-full rounded-md bg-[#0A0D14] border border-white/10 text-white px-3 py-2 text-sm" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/70">Overlay Text</Label>
                                <Input value={form.overlay_text} onChange={e => setForm({ ...form, overlay_text: e.target.value })} placeholder="Text shown as a quote overlay" className="bg-[#0A0D14] border-white/10 text-white" />
                            </div>
                            <div className="flex gap-3">
                                <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-ar-asset-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Asset
                                </Button>
                                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white/60">Cancel</Button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-12 text-white/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                ) : assets.length === 0 ? (
                    <div className="text-center py-16 bg-[#12151C] rounded-lg border border-white/5">
                        <Eye className="w-12 h-12 text-white/10 mx-auto mb-3" />
                        <p className="text-white/40">No AR assets yet. Add historical images, reconstructions, or info popups.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {assets.map((asset, idx) => (
                            <div key={asset.id} className="bg-[#12151C] border border-white/10 rounded-lg overflow-hidden" data-testid={`ar-asset-card-${asset.id}`}>
                                {asset.image_url && (
                                    <div className="relative h-40 bg-[#0A0D14]">
                                        <img src={asset.image_url} alt={asset.title} className="w-full h-full object-cover" />
                                        {asset.year_label && (
                                            <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 rounded-full px-2 py-0.5 text-xs">
                                                <Clock className="w-3 h-3 text-[#D4AF37]" />
                                                <span className="text-[#D4AF37] font-bold">{asset.year_label}</span>
                                            </div>
                                        )}
                                        <div className="absolute top-2 right-2">
                                            <span className="bg-purple-500/30 text-purple-300 text-xs font-medium px-2 py-0.5 rounded-full capitalize">
                                                {asset.type?.replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className="p-4">
                                    <h4 className="font-semibold text-white text-sm mb-1">{asset.title}</h4>
                                    {asset.description && <p className="text-xs text-white/40 mb-2 line-clamp-2">{asset.description}</p>}
                                    {asset.overlay_text && (
                                        <p className="text-xs text-white/50 italic border-l-2 border-purple-400/50 pl-2 mb-3">{asset.overlay_text}</p>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(asset.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-ar-${asset.id}`}>
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
